import { CommonModule } from "@angular/common";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import {
  ErrorHandler,
  Injector,
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MomentDateAdapter } from "@angular/material-moment-adapter";
import { MatBadgeModule } from "@angular/material/badge";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatInputModule } from "@angular/material/input";
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from "@angular/material/snack-bar";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { BrowserAnimationsModule, provideAnimations } from "@angular/platform-browser/animations";
import { Router, RouterModule } from "@angular/router";
import { FullCalendarModule } from "@fullcalendar/angular";
import { MtxDatetimepickerModule } from "@ng-matero/extensions/datetimepicker";
import { MtxPopoverModule } from "@ng-matero/extensions/popover";
import { FormlyModule } from "@ngx-formly/core";
import { NgxsReduxDevtoolsPluginModule } from "@ngxs/devtools-plugin";
import { NgxsModule, Store } from "@ngxs/store";
import { RxReactiveFormsModule } from "@rxweb/reactive-form-validators";
import { ApplicationState, EventAggregator } from "@vp/data-access/application";
import {
  CASE_SUMMARY_API_BASE_URL,
  CaseApiService,
  CaseState,
  CaseSummaryService
} from "@vp/data-access/case";
import { CASE_TYPES_API_BASE_URL, CaseTypesState } from "@vp/data-access/case-types";
import {
  CONTENT_MANAGEMENT_API_BASE_URL,
  ContentApiService,
  ContentFilterState
} from "@vp/data-access/content-management";
import { MessagesState } from "@vp/data-access/message";
import { OrganizationState } from "@vp/data-access/organization";
import { RequestMetaDataState } from "@vp/data-access/request-meta-data";
import { TAG_API_BASE_URL, TagsApiService, TagsState } from "@vp/data-access/tags";
import { USER_API_BASE_URL, UserApiService, UserState } from "@vp/data-access/users";
import { FormlyJsonSchemaModule, JsonSchemaValueProviderService } from "@vp/formly/json-schema";
import { FORM_STATE_PROVIDERS } from "@vp/formly/ui-schema-config";
import { AuthenticationHeaderInterceptor, ServerErrorInterceptor } from "@vp/interceptors";
import { AJV_CLASS, AJV_INSTANCE, JsonSchemaValidationService } from "@vp/json-schema-validation";
import { AccessControlService } from "@vp/shared/access-control";
import { AppInsightsProxyService } from "@vp/shared/app-insights-proxy-service";
import { AuthenticationService } from "@vp/shared/authentication";
import { BottomNavShellModule } from "@vp/shared/bottom-nav/ui/bottom-nav-shell";
import { BreadcrumbModule } from "@vp/shared/components/breadcrumb";
import { PageMenuModule } from "@vp/shared/components/page-menu";
import { SecurityPopupModule } from "@vp/shared/components/security-popup";
import { CybersourceModule } from "@vp/shared/cybersouce";
import { FlexModule } from "@vp/shared/directives/flex";
import { FeatureInterceptor, FeatureService, FeaturesModule } from "@vp/shared/features";
import {
  CasetypesFormStateProviderService,
  ConditionalReadOnlyFieldsFormStateProviderService,
  ContentTagFormStateProviderService,
  CountryDropdownFormStateProviderService,
  CountryTagFormStateProviderService,
  CurrentUserFormStateProviderService,
  RolesFormStateProviderService,
  TagsFormStateProviderService
} from "@vp/shared/form-state-providers";
import {
  AuthenticationCanActivateGuard,
  AuthenticationGuardService,
  UserProfileGuard,
  UserProfileGuardService
} from "@vp/shared/guards";
import {
  LOCAL_STORAGE_OPTIONS,
  LocalStorageModule,
  LocalStorageService
} from "@vp/shared/local-storage";
import { LocaleService } from "@vp/shared/locale-service";
import { ConsoleLoggingService, LoggerService } from "@vp/shared/logger-service";
import { MaterialModule } from "@vp/shared/material";
import { NotificationService } from "@vp/shared/notification-service";
import {
  PERMISSIONS_CONST_ENVIRONMENT_IS_PROD,
  PermissionsConstService
} from "@vp/shared/permissions-const";
import { ContextDisplayModule } from "@vp/shared/pipes/context-display";
import { SignalRApiService, SignalRState } from "@vp/shared/signal-r-service";
import { HttpStateService, SpinnerInterceptorService, SpinnerModule } from "@vp/shared/spinner";
import { APP_STORE_API_BASE_URL, AppStoreService } from "@vp/shared/store/app";
import { UiDisplayTagService } from "@vp/shared/store/ui";
import { SystemBannerModule } from "@vp/shared/system-banner";
import {
  ALLOW_REGISTRATION,
  AMBRA_BASE_URL,
  ANGULAR_EDITOR_CONFIG,
  ANON_CONFIG,
  APIM_SUB_KEY,
  APIM_TRACE,
  API_BASE_URL,
  APP_COPYRIGHT,
  APP_INSIGHTS_KEY,
  FUNC_ACCT_STOR_NAME,
  OKTA_CONSUMER_ENV,
  OKTA_LOGIN_BRAND,
  OKTA_ROUTES,
  TINYMCE_API_KEY,
  TINYMCE_DEFAULT_CONFIG
} from "@vp/shared/tokens";
import { TopNavFeatureModule } from "@vp/shared/top-nav/feature";
import { TopNavShellModule } from "@vp/shared/top-nav/ui/top-nav-shell";
import { AppSettingsResolverService, UTILITY_OPTIONS } from "@vp/shared/utilities";
import { TagManagerState } from "@vp/tag-manager/data-access/tag-manager-state";
import { ThemeManagerModule } from "@vp/theme-manager";
import {
  ADVANCED_COMMANDS_API_BASE_URL,
  AdvancedCommandsService
} from "@vp/virtual-care/data-access";
import { default as Ajv, default as ajv } from "ajv";
import { AuthModule, ConfigurationService, LogLevel } from "angular-auth-oidc-client";
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from "ngx-mask";
import { NgxPermissionsModule, NgxPermissionsService } from "ngx-permissions";
import { ToastrModule } from "ngx-toastr";
import { accessControlFactory } from "./app/access-control.factory";
import { angularEditorConfigDefault, configureAuth, tinyMCEDefaultConfig } from "./app/app-config";
import { CONFIG_DEPENDENCIES, appInitializerFactory } from "./app/app-initializer.factory";
import { AppRoutingModule } from "./app/app-routing.module";
import { appThemeManagerConfig } from "./app/app-theme-manager.config";
import { AppComponent } from "./app/app.component";
import { GlobalErrorHandler } from "./app/global-error-handler";
import { environment } from "./environments/environment";
import { config } from "./environments/environment.config";

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideAnimations(),
    importProvidersFrom(
      NgxsModule.forRoot(
        [
          ApplicationState,
          SignalRState,
          CaseState,
          RequestMetaDataState,
          ContentFilterState,
          CaseTypesState,
          OrganizationState,
          TagsState,
          UserState,
          MessagesState,
          TagManagerState
        ],
        {
          developmentMode: !environment.production,
          selectorOptions: { injectContainerState: false }
        }
      ),
      NgxsReduxDevtoolsPluginModule.forRoot(),
      AppRoutingModule,
      AuthModule.forRoot({
        config: {
          authority: environment.authServer,
          redirectUrl: `${window.location.origin}/login/callback`,
          clientId: environment.oktaClientId,
          scope: "openid profile email",
          responseType: "code",
          postLogoutRedirectUri: `${window.location.origin}/loggedout`,
          startCheckSession: false,
          silentRenew: true,
          silentRenewUrl: `${window.location.origin}/silent-renew.html`,
          postLoginRoute: "/loggedin",
          forbiddenRoute: "/forbidden",
          unauthorizedRoute: "/login",
          logLevel: LogLevel.Warn,
          historyCleanupOff: true,
          disableIatOffsetValidation: true,
          renewTimeBeforeTokenExpiresInSeconds: 120
        }
      }),
      CommonModule,
      BrowserAnimationsModule,
      BrowserModule,
      BreadcrumbModule,
      FeaturesModule,
      FormsModule,
      ReactiveFormsModule,
      FormlyModule,
      MatBadgeModule,
      MatInputModule,
      MaterialModule,
      MtxPopoverModule,
      MtxDatetimepickerModule,
      PageMenuModule,
      RouterModule,
      MaterialModule,
      SpinnerModule,
      ContextDisplayModule,
      FlexModule,
      FullCalendarModule,
      HttpClientModule,
      LocalStorageModule,
      MatDatepickerModule,
      MaterialModule,
      RxReactiveFormsModule,
      SpinnerModule,
      ToastrModule.forRoot({
        timeOut: 5000,
        positionClass: "toast-top-right",
        preventDuplicates: true
      }),
      NgxPermissionsModule.forRoot(),
      NgxMaskDirective,
      NgxMaskPipe,
      ThemeManagerModule.forRoot(appThemeManagerConfig),
      FeaturesModule,
      CybersourceModule,
      TopNavFeatureModule,
      TopNavShellModule,
      BottomNavShellModule,
      SecurityPopupModule,
      SystemBannerModule,
      FormlyJsonSchemaModule.forRoot({
        providers: [],
        uischema: {
          layouts: [
            {
              types: ["payment"],
              elements: [
                { scope: "#/properties/payment", definition: "payment-page", formlyConfig: {} }
              ]
            },
            {
              types: ["images"],
              elements: [{ scope: "#/properties/images", definition: "dicoms" }]
            },
            {
              types: ["documents"],
              elements: [{ scope: "#/properties/documents", definition: "documents" }]
            }
          ],
          definitions: [
            {
              types: ["email-field"],
              formlyConfig: {
                templateOptions: { type: "email", maxLength: 255 },
                validators: { validation: ["email"] }
              }
            },
            {
              types: ["facesheet-selector"],
              formlyConfig: {
                type: "flex-layout",
                key: "facesheet",
                className: "flex-column",
                templateOptions: {
                  label: "Facesheet Upload"
                },
                fieldGroup: [
                  {
                    key: "selUpload",
                    type: "checkbox",
                    templateOptions: { label: "Check for facesheet upload." }
                  },
                  {
                    key: "facesheet-actions",
                    type: "facesheet-selector",
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    hideExpression: (model: any) => {
                      console.log(JSON.stringify(model));
                      return !model?.selUpload;
                    }
                  }
                ]
              }
            },
            {
              types: ["attachments"],
              formlyConfig: {
                name: "attachments",
                props: { label: "Attachments", order: 2 },
                type: "flex-layout",
                className: "flex-column",
                fieldGroup: [
                  { key: "document-data", type: "document-data", className: "flex100" },
                  {
                    key: "document-actions",
                    type: "document-actions",
                    className: "padding-right"
                  },
                  { key: "document-grid", type: "document-grid", className: "padding-right" },
                  { key: "image-actions", type: "image-actions", className: "padding-right" },
                  { key: "image-grid", type: "image-grid", className: "padding-right" }
                ]
              }
            },
            {
              types: ["documents"],
              formlyConfig: {
                name: "documents",
                type: "flex-layout",
                className: "flex-column",
                templateOptions: { label: "Documents", order: 2 },
                fieldGroup: [
                  { key: "document-data", type: "document-data", className: "flex100" },
                  {
                    key: "document-actions",
                    type: "document-actions",
                    className: "padding-right"
                  },
                  { key: "document-grid", type: "document-grid", className: "flex100" }
                ]
              }
            },
            {
              types: ["images"],
              formlyConfig: {
                name: "images",
                type: "flex-layout",
                className: "flex-column",
                props: { label: "Images", order: 2 },
                fieldGroup: [
                  { key: "image-data", type: "image-data", className: "flex100" },
                  { key: "image-actions", type: "image-actions", className: "flex100" },
                  { key: "image-grid", type: "image-grid", className: "flex100" }
                ]
              }
            },
            {
              types: ["dicoms"],
              formlyConfig: {
                name: "dicoms",
                type: "flex-layout",
                className: "flex-column",
                props: { label: "Imaging", order: 2 },
                fieldGroup: [
                  {
                    key: "dicoms-data",
                    type: "image-data", // Reusing image-data component
                    className: "flex100"
                  },
                  {
                    template: `
                     <div style='margin: 16px !important'>
                       <div>
                         <span><strong>1.</strong></span>
                         Please place the CD in the CD drive of your computer, or insert the USB Flash Drive you were given after receiving radiology imaging back from the facility you had radiologic imaging done.
                       </div>
                       <div>
                         <span><strong>2.</strong></span>
                         Close any other browser windows.
                       </div>
                       <div>
                         <span><strong>3.</strong></span>
                         Click on "Upload DICOM Folder" button.
                       </div>
                       <div>
                         <span ><strong>4.</strong></span>
                         Click Select Folder from your CD or USB Flash Drive.
                       </div>
                       <div>
                         <span><strong>5.</strong></span>
                         If the facility you had imaging done provided you with a download link please ensure the files are downloaded and unzipped. Export the files to a folder on your desktop and select the folder.
                       </div>
                       <div>
                         <span><strong>6.</strong></span>
                         Click the Upload button.
                       </div>
                     </div>
                     `,
                    className: "flexOffset8px"
                  },
                  { key: "dicoms-actions", type: "dicoms-actions", className: "padding-right" },
                  { key: "dicoms-grid", type: "dicoms-grid", className: "padding-right" }
                ]
              }
            },
            {
              types: ["assignments"],
              formlyConfig: {
                name: "assignments",
                className: "flex-row-wrap",
                templateOptions: { label: "Assignments", order: 3 },
                fieldGroup: [{ key: "assign-groups", type: "assign-groups", className: "flex100" }]
              }
            },
            {
              types: ["state-selector"],
              formlyConfig: {
                type: "select",
                templateOptions: {
                  options: [
                    { label: "Alabama", value: "AL" },
                    { label: "Alaska", value: "AK" },
                    { label: "Arizona", value: "AZ" },
                    { label: "Arkansas", value: "AR" },
                    { label: "California", value: "CA" },
                    { label: "Colorado", value: "CO" },
                    { label: "Connecticut", value: "CT" },
                    { label: "Delaware", value: "DE" },
                    { label: "District Of Columbia", value: "DC" },
                    { label: "Florida", value: "FL" },
                    { label: "Georgia", value: "GA" },
                    { label: "Guam", value: "GU" },
                    { label: "Hawaii", value: "HI" },
                    { label: "Idaho", value: "ID" },
                    { label: "Illinois", value: "IL" },
                    { label: "Indiana", value: "IN" },
                    { label: "Iowa", value: "IA" },
                    { label: "Kansas", value: "KS" },
                    { label: "Kentucky", value: "KY" },
                    { label: "Louisiana", value: "LA" },
                    { label: "Maine", value: "ME" },
                    { label: "Marshall Islands", value: "MH" },
                    { label: "Maryland", value: "MD" },
                    { label: "Massachusetts", value: "MA" },
                    { label: "Michigan", value: "MI" },
                    { label: "Minnesota", value: "MN" },
                    { label: "Mississippi", value: "MS" },
                    { label: "Missouri", value: "MO" },
                    { label: "Montana", value: "MT" },
                    { label: "Nebraska", value: "NE" },
                    { label: "Nevada", value: "NV" },
                    { label: "New Hampshire", value: "NH" },
                    { label: "New Jersey", value: "NJ" },
                    { label: "New Mexico", value: "NM" },
                    { label: "New York", value: "NY" },
                    { label: "North Carolina", value: "NC" },
                    { label: "North Dakota", value: "ND" },
                    { label: "Northern Mariana Islands", value: "MP" },
                    { label: "Ohio", value: "OH" },
                    { label: "Oklahoma", value: "OK" },
                    { label: "Oregon", value: "OR" },
                    { label: "Palau", value: "PW" },
                    { label: "Pennsylvania", value: "PA" },
                    { label: "Puerto Rico", value: "PR" },
                    { label: "Rhode Island", value: "RI" },
                    { label: "South Carolina", value: "SC" },
                    { label: "South Dakota", value: "SD" },
                    { label: "Tennessee", value: "TN" },
                    { label: "Texas", value: "TX" },
                    { label: "Utah", value: "UT" },
                    { label: "Vermont", value: "VT" },
                    { label: "Virgin Islands", value: "VI" },
                    { label: "Virginia", value: "VA" },
                    { label: "Washington", value: "WA" },
                    { label: "West Virginia", value: "WV" },
                    { label: "Wisconsin", value: "WI" },
                    { label: "Wyoming", value: "WY" }
                  ]
                }
              }
            },
            {
              types: ["payment-page"],
              formlyConfig: {
                type: "flex-layout",
                key: "payment",
                name: "payment",
                templateOptions: { label: "Payment", actions: { payment: { submit: true } } },
                fieldGroup: [{ key: "cybersource", type: "cybersource-payment" }]
              }
            }
          ]
        }
      }),
      FormlyModule.forRoot({
        extras: { checkExpressionOn: "modelChange" },
        validators: [
          { name: "requiredTrue", validation: Validators.requiredTrue },
          { name: "email", validation: Validators.email }
        ],
        validationMessages: [{ name: "email", message: "A Valid email address is required" }],
        types: []
      })
    ),

    { provide: ALLOW_REGISTRATION, useValue: config.allowRegistration },
    { provide: APP_COPYRIGHT, useValue: config.appCopyright },
    { provide: API_BASE_URL, useValue: config.baseApi },
    { provide: OKTA_ROUTES, useValue: config.oktaApiRoutes },
    { provide: APIM_TRACE, useValue: config.apimTrace },
    { provide: APIM_SUB_KEY, useValue: config.apimSubscriptionKey },
    { provide: APP_INSIGHTS_KEY, useValue: config.appInsightsKey },
    { provide: FUNC_ACCT_STOR_NAME, useValue: config.functionStorageAccountName },
    { provide: OKTA_LOGIN_BRAND, useValue: config.oktaLoginBrand },
    { provide: OKTA_CONSUMER_ENV, useValue: config.consumerOktaEnv },
    { provide: AMBRA_BASE_URL, useValue: config.ambraViewerBaseUrl },
    { provide: APP_STORE_API_BASE_URL, useValue: config.baseApi },
    { provide: CASE_SUMMARY_API_BASE_URL, useValue: config.baseApi },
    { provide: CASE_TYPES_API_BASE_URL, useValue: `${config.baseApi}/casetype` },
    { provide: PERMISSIONS_CONST_ENVIRONMENT_IS_PROD, useValue: environment.production },
    { provide: TAG_API_BASE_URL, useValue: `${config.baseApi}/tags` },
    { provide: USER_API_BASE_URL, useValue: config.baseApi },
    { provide: CONTENT_MANAGEMENT_API_BASE_URL, useValue: config.baseApi },
    { provide: ADVANCED_COMMANDS_API_BASE_URL, useValue: config.baseApi },
    { provide: HTTP_INTERCEPTORS, useClass: ServerErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthenticationHeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: FeatureInterceptor, multi: true, deps: [Store] },
    { provide: TINYMCE_API_KEY, useValue: config.tinyMceApiKey },
    { provide: TINYMCE_DEFAULT_CONFIG, useValue: tinyMCEDefaultConfig },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptorService,
      multi: true,
      deps: [HttpStateService]
    },
    ConfigurationService,
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 3000 } },
    { provide: AJV_CLASS, useValue: ajv },
    {
      provide: AJV_INSTANCE,
      useFactory: (ajvClass: typeof Ajv) => {
        // TODO - remove schema property in database before turning on strict mode
        return new ajvClass({ strict: false });
      },
      deps: [AJV_CLASS]
    },
    { provide: ANGULAR_EDITOR_CONFIG, useValue: angularEditorConfigDefault },
    { provide: EventAggregator, useClass: EventAggregator, deps: [Store] },
    { provide: LoggerService, useClass: LoggerService, deps: [Store] },

    { provide: ErrorHandler, useClass: GlobalErrorHandler, deps: [LoggerService, EventAggregator] },

    {
      provide: ConsoleLoggingService,
      useClass: ConsoleLoggingService,
      deps: [EventAggregator, ApplicationState]
    },

    {
      provide: AppInsightsProxyService,
      useClass: AppInsightsProxyService,
      deps: [APP_INSIGHTS_KEY, EventAggregator, Store]
    },
    {
      provide: NotificationService,
      useClass: NotificationService,
      deps: [Injector, EventAggregator, FeatureService]
    },
    provideAppInitializer(() => {
      inject(AppInsightsProxyService).init();
    }),
    provideAppInitializer(() => {
      configureAuth();
      return Promise.resolve();
    }),
    provideAppInitializer(() => {
      const accessControl = inject(AccessControlService);
      const appStore = inject(AppStoreService);
      const auth = inject(AuthenticationService);
      const configDeps = inject(CONFIG_DEPENDENCIES);
      const featureService = inject(FeatureService);
      const isAnonApi = !!inject(ANON_CONFIG);
      const localStorage = inject(LocalStorageService);
      const permsConst = inject(PermissionsConstService);
      const router = inject(Router);
      const store = inject(Store);
      const uiDisplay = inject(UiDisplayTagService);

      return appInitializerFactory(
        accessControl,
        appStore,
        auth,
        configDeps,
        featureService,
        isAnonApi,
        localStorage,
        permsConst,
        router,
        store,
        uiDisplay
      )();
    }),
    {
      provide: AccessControlService,
      deps: [FeatureService, AppStoreService, LoggerService, NgxPermissionsService, Router]
    },

    { provide: UserProfileGuard, useClass: UserProfileGuardService },
    { provide: AuthenticationCanActivateGuard, useClass: AuthenticationGuardService },
    {
      provide: CONFIG_DEPENDENCIES,
      // Use a factory that return an array of dependant functions to be executed
      useFactory: (accessControlService: AccessControlService) => {
        // Easy to add or remove dependencies
        return [accessControlFactory(accessControlService)];
      },
      deps: [AccessControlService]
    },
    {
      provide: ANON_CONFIG,
      useFactory: () => {
        const fromHost = window.location.href;
        return environment.anonymousDomains.find(x => fromHost.includes(x.url));
      }
    },
    JsonSchemaValueProviderService,
    { provide: UTILITY_OPTIONS, useValue: { routeDefault: "/home" } },
    {
      provide: MAT_DATE_FORMATS,
      useFactory: (localeService: LocaleService) => {
        return localeService.getDateFormat();
      },
      deps: [LocaleService]
    },
    { provide: LOCAL_STORAGE_OPTIONS, useValue: { storageName: "app", maxAgeSeconds: 600 } },
    {
      provide: FORM_STATE_PROVIDERS,
      useClass: TagsFormStateProviderService,
      multi: true,
      deps: [TagsApiService, Store, TagsState]
    },
    {
      provide: FORM_STATE_PROVIDERS,
      useClass: RolesFormStateProviderService,
      multi: true,
      deps: [Store, OrganizationState]
    },
    {
      provide: FORM_STATE_PROVIDERS,
      useClass: CountryTagFormStateProviderService,
      multi: true,
      deps: [Store, TagsState, ApplicationState]
    },
    {
      provide: FORM_STATE_PROVIDERS,
      useClass: CountryDropdownFormStateProviderService,
      multi: true
    },
    { provide: FORM_STATE_PROVIDERS, useClass: ContentTagFormStateProviderService, multi: true },
    { provide: FORM_STATE_PROVIDERS, useClass: CasetypesFormStateProviderService, multi: true },
    {
      provide: FORM_STATE_PROVIDERS,
      useClass: ConditionalReadOnlyFieldsFormStateProviderService,
      multi: true,
      deps: [Store, TagManagerState, OrganizationState, ApplicationState]
    },
    {
      provide: FORM_STATE_PROVIDERS,
      useClass: CurrentUserFormStateProviderService,
      multi: true,
      deps: [Store]
    },
    AppSettingsResolverService,
    JsonSchemaValidationService,
    AdvancedCommandsService,
    AppStoreService,
    SignalRApiService,
    TagsApiService,
    CaseApiService,
    ContentApiService,
    CaseSummaryService,
    PermissionsConstService,
    UserApiService,
    provideNgxMask()
  ]
}).catch(err => console.error(err));
